import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { isClickedOutside, onAnimationEnd } from '@theme/utilities';

const ITI_VERSION = '23.7.4';
const ITI_CSS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/css/intlTelInput.min.css`;
const ITI_JS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/intlTelInput.min.js`;
const ITI_UTILS = `https://cdn.jsdelivr.net/npm/intl-tel-input@${ITI_VERSION}/build/js/utils.js`;

const PENDING_LEAD_KEY = 'obp_pending_lead';
const DATALAYER_EVENT = 'generate_lead';
const LEAD_SOURCE = 'product_page_request_quote';

/** Matches Google Ads' 90-day click lookback window. */
const CLICK_ID_STORE = 'sofaworx_click_ids';
const CLICK_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;

let itiPromise = null;

function loadIntlTelInput() {
  if (itiPromise) return itiPromise;

  if (!document.querySelector(`link[href="${ITI_CSS}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = ITI_CSS;
    document.head.appendChild(link);
  }

  itiPromise = new Promise((resolve, reject) => {
    if (typeof window.intlTelInput === 'function') return resolve();

    const existing = document.querySelector(`script[src="${ITI_JS}"]`);
    const script = existing ?? document.createElement('script');

    const onLoad = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      resolve();
    };
    const onError = () => {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      itiPromise = null;
      reject(new Error('intl-tel-input failed to load'));
    };

    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);

    if (!existing) {
      script.src = ITI_JS;
      script.async = true;
      document.head.appendChild(script);
    }
  });

  return itiPromise;
}

function generateEventId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Parse a displayed money string ("R 20,095.00") to a number, or null.
 * The quoted price shown in the dialog tracks the selected fabric (BOM
 * pricing), unlike the render-time variant price — tracking must prefer it.
 */
function parseMoney(text) {
  if (!text) return null;
  const n = Number(String(text).replace(/[^\d.]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]+)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Ad click ids captured on landing by the click-id-capture snippet.
 *
 * Google's _gcl_* cookies are written by the conversion linker inside gtag/GTM.
 * With no container on the site, a visitor who lands on ?gclid=... and browses
 * before converting has neither the URL parameter nor the cookie by the time
 * the form submits, so the conversion cannot be tied back to the click.
 */
function readStoredClickIds() {
  try {
    const raw = localStorage.getItem(CLICK_ID_STORE);
    if (!raw) return {};
    const parsed = JSON.parse(raw) || {};
    if (parsed.ts && Date.now() - parsed.ts > CLICK_ID_TTL_MS) {
      localStorage.removeItem(CLICK_ID_STORE);
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/**
 * GA4 session id from a _ga_<STREAM> cookie value.
 *
 * Google changed this cookie on 6 May 2025 and both shapes are still live:
 *   GS1.1.<session_id>.<n>...          legacy, dot-delimited
 *   GS2.1.s<session_id>$o1$g1$t...     current, $-delimited
 *
 * Reading only the legacy shape returns nothing on a current property, and a
 * Measurement Protocol event with no session id does not contribute correctly
 * to session-scoped metrics or attribution.
 */
function parseGaSessionId(value) {
  if (!value) return undefined;
  const gs2 = value.match(/(?:^|[.$])s(\d+)(?:[$.]|$)/);
  if (gs2) return gs2[1];
  const seg = value.split('.');
  if (seg.length >= 3 && /^\d+$/.test(seg[2])) return seg[2];
  return undefined;
}

/**
 * Browser attribution ids for server-side conversion events (Meta CAPI +
 * GA4 Measurement Protocol). Everything is best-effort — a missing cookie
 * just lowers match quality, it never blocks the lead.
 */
function collectAttribution() {
  const params = new URLSearchParams(window.location.search);
  const stored = readStoredClickIds();

  // Meta: _fbp always set by the pixel; _fbc only after an fb ad click.
  // When _fbc is absent but the landing URL carries fbclid, Meta's documented
  // fallback format is fb.1.<ms timestamp>.<fbclid>.
  const fbp = getCookie('_fbp') || undefined;
  let fbc = getCookie('_fbc') || undefined;
  const fbclid = params.get('fbclid');
  if (!fbc && fbclid) {
    fbc = `fb.1.${Date.now()}.${fbclid}`;
  } else if (!fbc && stored.fbclid) {
    // Stamp the click with when it was actually seen, not when it converted.
    fbc = `fb.1.${stored.ts || Date.now()}.${stored.fbclid}`;
  }

  // GA4: client id = last two segments of _ga ("GA1.1.123456.789" → "123456.789").
  let gaClientId;
  const ga = getCookie('_ga');
  if (ga) {
    const parts = ga.split('.');
    if (parts.length >= 4) gaClientId = `${parts[2]}.${parts[3]}`;
  }

  // A property can carry more than one _ga_<STREAM> cookie; take the first that
  // yields a session id rather than assuming the first cookie is the right one.
  let gaSessionId;
  for (const c of document.cookie.split('; ')) {
    if (!c.startsWith('_ga_') || !c.includes('=')) continue;
    const value = decodeURIComponent(c.slice(c.indexOf('=') + 1) || '');
    gaSessionId = parseGaSessionId(value);
    if (gaSessionId) break;
  }

  // Google Ads click ids: URL, then the id stored on landing, then Google's own
  // cookie. gbraid and wbraid cover the iOS app-to-web journeys gclid misses.
  let gclid = params.get('gclid') || stored.gclid || undefined;
  if (!gclid) {
    const gcl = getCookie('_gcl_aw');
    if (gcl) {
      const seg = gcl.split('.');
      if (seg.length >= 3) gclid = seg.slice(2).join('.');
    }
  }
  const gbraid = params.get('gbraid') || stored.gbraid || undefined;
  const wbraid = params.get('wbraid') || stored.wbraid || undefined;

  return {
    fbp,
    fbc,
    ga_client_id: gaClientId,
    ga_session_id: gaSessionId,
    gclid,
    gbraid,
    wbraid,
    user_agent: navigator.userAgent,
    referrer: document.referrer || undefined,
    landing_page: window.location.href,
  };
}

function safeStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export class OrderByPhoneComponent extends Component {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  /** @type {ReturnType<typeof window.intlTelInput> | null} */
  #iti = null;

  /** @type {Record<string, string>} */
  #variantSnapshot = {};

  /** @type {Record<string, any> | null} */
  #productData = null;

  #previousScrollY = 0;

  #isClosing = false;

  connectedCallback() {
    super.connectedCallback();

    console.info('[obp] connectedCallback start', {
      hasDialogInside: !!this.querySelector('dialog[data-obp-dialog]'),
      hasFormTemplate: !!this.querySelector('script[data-obp-form-template]'),
    });

    // The component renders inside Shopify's product <form> (via buy-buttons). HTML
    // disallows nested <form> elements — the browser silently strips the inner one
    // during parse. Workaround: ship the contact form as raw text inside a
    // <script type="text/html"> template; hydrate it via JS into the dialog, then
    // portal the dialog out of the product form to body.
    this.#hydrateForm();
    this.#portalDialog();
    this.#productData = this.#readProductData();
    this.#captureVariantSnapshotFromDom();

    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    if (this.#dialog) {
      const closeBtn = this.#dialog.querySelector('[data-obp-close]');
      const form = this.#dialog.querySelector('form');
      console.info('[obp] after portal', {
        dialogInBody: this.#dialog.parentNode === document.body,
        foundCloseBtn: !!closeBtn,
        foundForm: !!form,
        formAction: form?.getAttribute('action'),
        formMethod: form?.getAttribute('method'),
      });
      closeBtn?.addEventListener('click', this.closeDialog);
      form?.addEventListener('submit', this.#handleSubmit);
      this.#dialog.addEventListener('click', this.#handleBackdropClick);
    } else {
      console.warn('[obp] no dialog found after portal — submit handler NOT attached');
    }

    if (this.#dialog?.querySelector('.order-by-phone-form__success, .order-by-phone-form__error')) {
      requestAnimationFrame(() => this.showDialog());
    }

    if (this.#dialog?.querySelector('.order-by-phone-form__success')) {
      this.#replayPendingLead();
    }
  }

  #hydrateForm() {
    const template = this.querySelector('script[data-obp-form-template]');
    const mount = this.querySelector('[data-obp-form-mount]');
    if (!template || !mount) {
      console.warn('[obp] hydrateForm — missing template or mount', { template: !!template, mount: !!mount });
      return;
    }

    // Parse the form HTML in an isolated container — no parent <form> to trigger
    // the HTML parser's "no nested forms" stripping.
    const container = document.createElement('div');
    container.innerHTML = template.textContent?.trim() || '';
    const form = container.querySelector('form');
    if (!form) {
      console.warn('[obp] hydrateForm — no <form> in parsed template');
      return;
    }

    mount.replaceWith(form);
    console.info('[obp] form hydrated', { action: form.getAttribute('action') });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    document.removeEventListener('keydown', this.#handleKeyDown);
    if (this.#dialog?.parentNode === document.body) {
      this.#dialog.remove();
    }
  }

  #portalDialog() {
    const dialog = this.querySelector('dialog[data-obp-dialog]');
    if (!dialog || !(dialog instanceof HTMLDialogElement)) return;
    document.body.appendChild(dialog);
    this.#dialog = dialog;
  }

  // ────────────────────────────────────────────────────────────────
  // Dialog open/close (manual since dialog is portaled out of the
  // component subtree, so DialogComponent's ref-based methods don't apply).
  // ────────────────────────────────────────────────────────────────

  showDialog = async () => {
    if (!this.#dialog || this.#dialog.open) return;

    // Rehydrate the quantity + fabric/price from the page each time the dialog
    // opens — the stepper and Mira fabric picker live in the product section,
    // behind the modal, so their values are fixed for the open dialog.
    this.#syncQuantityFromPage();
    this.#syncVariantFromPage();
    this.#syncImageFromPage();

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;
      this.#dialog?.showModal();
      document.addEventListener('keydown', this.#handleKeyDown);
    });

    await this.#initPhoneInput();
  };

  closeDialog = async () => {
    if (!this.#dialog || !this.#dialog.open || this.#isClosing) return;
    this.#isClosing = true;

    const dialog = this.#dialog;
    document.removeEventListener('keydown', this.#handleKeyDown);

    // Re-trigger the closing animation
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } catch {
      /* ignore */
    }

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.#isClosing = false;
  };

  #handleBackdropClick = (event) => {
    if (!this.#dialog) return;
    if (isClickedOutside(event, this.#dialog.firstElementChild || this.#dialog)) {
      this.closeDialog();
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.closeDialog();
  };

  // ────────────────────────────────────────────────────────────────

  async #initPhoneInput() {
    if (this.#iti) return;
    const phoneInput = /** @type {HTMLInputElement | null} */ (
      this.#dialog?.querySelector('[ref="phoneInput"]') || null
    );
    if (!phoneInput) return;

    try {
      await loadIntlTelInput();
      if (typeof window.intlTelInput !== 'function') throw new Error('intlTelInput not on window');
      this.#iti = window.intlTelInput(phoneInput, {
        initialCountry: 'za',
        preferredCountries: ['za', 'gb', 'us'],
        separateDialCode: true,
        autoPlaceholder: 'off',
        utilsScript: ITI_UTILS,
      });
    } catch (e) {
      console.warn('[order-by-phone] intl-tel-input failed to load; using plain tel input', e);
    }
  }

  #readProductData() {
    const script = this.querySelector('script[data-obp-product]');
    if (!script || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[order-by-phone] product JSON parse failed', e);
      return null;
    }
  }

  #handleVariantUpdate = (event) => {
    const variant = event?.detail?.resource;
    if (!variant) return;
    if (event.detail?.data?.productId && event.detail.data.productId !== this.#productId()) return;

    this.#updateVariantDisplay(variant);

    if (this.#productData) {
      this.#productData.variant_id = variant.id;
      this.#productData.variant_title = variant.title;
      this.#productData.variant_sku = variant.sku || this.#productData.variant_sku;
      if (typeof variant.price === 'number') {
        this.#productData.price = variant.price / 100;
      }
    }
  };

  #productId() {
    return this.closest('[data-product-id]')?.getAttribute('data-product-id') || null;
  }

  #captureVariantSnapshotFromDom() {
    const optionsEl = this.#dialog?.querySelector('[ref="productOptions"]');
    if (!optionsEl) return;
    /** @type {Record<string,string>} */
    const snapshot = {};
    optionsEl.querySelectorAll('.order-by-phone-dialog__option').forEach((opt) => {
      const name = opt.querySelector('dt')?.textContent?.trim();
      const value = opt.querySelector('dd')?.textContent?.trim();
      if (name) snapshot[name] = value || '';
    });
    this.#variantSnapshot = snapshot;
  }

  #updateVariantDisplay(variant) {
    if (!variant || !this.#dialog) return;

    const optionsEl = this.#dialog.querySelector('[ref="productOptions"]');
    const priceEl = this.#dialog.querySelector('[ref="priceDisplay"]');

    if (optionsEl && Array.isArray(variant.options)) {
      const optionRows = optionsEl.querySelectorAll('.order-by-phone-dialog__option');
      const snapshot = {};
      optionRows.forEach((row, idx) => {
        const dd = row.querySelector('dd');
        const dt = row.querySelector('dt');
        if (dd && variant.options[idx] !== undefined) {
          dd.textContent = variant.options[idx];
        }
        if (dt) snapshot[dt.textContent?.trim() || ''] = variant.options[idx] || '';
      });
      this.#variantSnapshot = snapshot;
    }

    if (typeof variant.price === 'number' && priceEl) {
      priceEl.textContent = this.#formatMoney(variant.price);
    }
  }

  #formatMoney(cents) {
    try {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, '{{ amount }}');
      }
    } catch {
      /* fall through */
    }
    return `R ${(cents / 100).toFixed(2)}`;
  }

  #refIn(name) {
    return this.#dialog?.querySelector(`[ref="${name}"]`) || null;
  }

  #syncQuantityFromPage() {
    const display = this.#refIn('quantityDisplay');
    if (!display) return;
    const scope = this.closest('sofaworx-product') || this.closest('[data-product-id]') || document;
    const qtyInput = scope.querySelector('[data-qty-input], [data-qty-hidden], input[name="quantity"]');
    const qty = parseInt(qtyInput?.value ?? '', 10);
    display.textContent = String(qty > 0 ? qty : 1);
  }

  // The fabric picker is injected by the Mira app extension, which resolves the
  // Shopify variant only at add-to-cart time — so the theme's 'variant:update'
  // event never fires for a fabric change and the hidden variant input stays at
  // the server-rendered default. Instead, read the live selection straight from
  // the page DOM (Mira marks the chosen swatch '.mira-fabric-card--selected' and
  // reactively updates '[data-price-current]') each time the dialog opens.
  #syncVariantFromPage() {
    // Prefer the section, but fall back to a document-wide lookup: depending on
    // which host the Mira extension resolves (it tries the cart form and other
    // anchors), the injected picker can end up outside this <sofaworx-product>
    // element. There is only one product per page, so a global query is safe.
    const section = this.closest('sofaworx-product') || this.closest('[data-product-id]');
    const findIn = (selector) =>
      (section && section.querySelector(selector)) || document.querySelector(selector);

    // Fabric name — e.g. "Define 22-Ginger". Read the selected swatch first;
    // fall back to the picker's step-1 summary (Mira writes the chosen fabric
    // there too) so a markup change in either spot doesn't break the sync.
    const fabricName =
      findIn('.mira-fabric-card--selected .mira-fabric-card__name')?.textContent?.trim() ||
      findIn('.mira-step--done .mira-step__summary')?.textContent?.trim() ||
      findIn('.mira-step__summary')?.textContent?.trim();
    if (fabricName) {
      const optionsEl = this.#dialog?.querySelector('[ref="productOptions"]');
      const rows = optionsEl?.querySelectorAll('.order-by-phone-dialog__option');
      // Prefer the row whose label is "Fabric"; fall back to the first row.
      let targetDd = null;
      rows?.forEach((row) => {
        const name = row.querySelector('dt')?.textContent?.trim().toLowerCase();
        if (!targetDd && name === 'fabric') targetDd = row.querySelector('dd');
      });
      if (!targetDd && rows?.length) targetDd = rows[0].querySelector('dd');
      if (targetDd) {
        // Keep any trailing dimensions suffix the server rendered (e.g.
        // "(175x90x70cm)") since Mira's swatch name omits it.
        const dims = targetDd.textContent?.match(/\s*(\([^)]*\))\s*$/);
        targetDd.textContent = fabricName + (dims ? ` ${dims[1]}` : '');
      }
      if (this.#productData) this.#productData.variant_title = fabricName;
    }

    // Price — copy the live, Mira-updated text straight across. The Mira
    // viewer REWRITES the theme's price node (plain textContent, or discount
    // markup with .mira-price-current), which destroys the inner
    // [data-price-current] span — so probe the viewer's own current-price
    // span first, the intact span second, and the rewritten node's plain
    // text last.
    const priceText =
      findIn('.mira-price-current')?.textContent?.trim() ||
      findIn('[data-price-current]')?.textContent?.trim() ||
      findIn('.sofaworx-product__price')?.textContent?.trim() ||
      findIn('[data-price]')?.textContent?.trim();
    const priceEl = this.#refIn('priceDisplay');
    if (priceText && priceEl) priceEl.textContent = priceText;
  }

  // Swap the modal thumbnail to frame 0 of the selected fabric's Mira render.
  // The 360 viewer loads frames as `{cdnBase}/frames/{productId}/{materialId}/
  // {NNNN}.png` (frame 0 = 0000.png) and the selected swatch carries the
  // material id, so we can build the frame-0 URL for the chosen fabric without
  // hitting the frames API. Falls back to the server-rendered featured image.
  #syncImageFromPage() {
    const imgEl = /** @type {HTMLImageElement | null} */ (
      this.#dialog?.querySelector('.order-by-phone-dialog__product-image img') || null
    );
    if (!imgEl) return;

    const selectedMid = document
      .querySelector('.mira-fabric-card--selected')
      ?.getAttribute('data-material-id');
    const sampleFrame = /** @type {HTMLImageElement | null} */ (
      document.querySelector('.mira-360-viewer__frames img')
    );
    const sampleUrl =
      sampleFrame?.currentSrc || sampleFrame?.getAttribute('src') || sampleFrame?.dataset.frameSrc;
    if (!sampleUrl || !/\/frames\//.test(sampleUrl)) return;

    // Rebuild the path with the selected material id and frame 0. If we can't
    // isolate the material segment, normalise whatever frame is showing to 0.
    const match = sampleUrl.match(/^(.*\/frames\/[^/]+)\/[^/]+\/\d+\.png(?:\?.*)?$/);
    const frame0 =
      selectedMid && match
        ? `${match[1]}/${selectedMid}/0000.png`
        : sampleUrl.replace(/\/\d+\.png(\?.*)?$/, '/0000.png');
    if (!frame0) return;

    imgEl.src = frame0;
    // The server-rendered <img> ships a responsive srcset/sizes for the product
    // featured image — clear them so the browser honours our frame URL.
    imgEl.removeAttribute('srcset');
    imgEl.removeAttribute('sizes');
  }

  #handleSubmit = async (event) => {
    event.preventDefault();
    console.info('[obp] submit fired');

    if (!this.#dialog) {
      console.warn('[obp] submit aborted — no dialog reference');
      return;
    }

    const form = event?.target instanceof HTMLFormElement ? event.target : this.#dialog.querySelector('form');
    if (!form) {
      console.warn('[obp] submit aborted — no form element');
      return;
    }

    // Native constraint validation (required name/email + showroom) runs first.
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

    const first = /** @type {HTMLInputElement | null} */ (this.#refIn('firstName'))?.value?.trim() || '';
    const last = /** @type {HTMLInputElement | null} */ (this.#refIn('lastName'))?.value?.trim() || '';
    const email = /** @type {HTMLInputElement | null} */ (this.#refIn('emailInput'))?.value?.trim() || '';
    const message = /** @type {HTMLTextAreaElement | null} */ (this.#refIn('messageInput'))?.value?.trim() || '';
    const showroom = /** @type {HTMLSelectElement | null} */ (this.#refIn('showroomSelect'))?.value?.trim() || '';
    const honeypot = /** @type {HTMLInputElement | null} */ (this.#refIn('honeypot'))?.value?.trim() || '';
    const phoneInput = /** @type {HTMLInputElement | null} */ (this.#refIn('phoneInput'));

    let phoneValue = '';
    let phoneCountry = '';
    if (this.#iti) {
      if (typeof this.#iti.getNumber === 'function') phoneValue = this.#iti.getNumber() || '';
      if (typeof this.#iti.getSelectedCountryData === 'function') {
        phoneCountry = (this.#iti.getSelectedCountryData()?.iso2 || '').toLowerCase();
      }
    }
    if (!phoneValue) phoneValue = phoneInput?.value?.trim() || '';

    const summary = this.#buildProductSummary();
    const product = this.#productData;
    const details = this.#collectSelectionDetails();
    const eventId = generateEventId();
    // The dialog's displayed price follows the selected fabric (BOM quote);
    // product.price is the render-time variant price and can be stale.
    const quotedValue = parseMoney(details.priceDisplay);

    // Absolute product URL for the email (Liquid's product.url is relative).
    let productUrl = product?.url || '';
    if (productUrl.startsWith('/')) productUrl = window.location.origin + productUrl;

    // Analytics payload (GTM/Meta) — legacy shape plus the quoted price.
    const payload = {
      event_id: eventId,
      event_time: Math.floor(Date.now() / 1000),
      lead_source: LEAD_SOURCE,
      form_id: this.dataset.formId || '',
      price_display: details.priceDisplay,
      showroom,
      user: {
        first_name: first,
        last_name: last,
        email,
        phone: phoneValue,
        country: phoneCountry || 'za',
      },
      product: product || null,
      variant_options: this.#variantSnapshot,
      message_provided: Boolean(message),
      page: {
        url: window.location.href,
        path: window.location.pathname,
        referrer: document.referrer || '',
      },
      meta: {
        fbp: getCookie('_fbp'),
        fbc: getCookie('_fbc'),
        user_agent: navigator.userAgent,
      },
    };

    // Lead payload for the Mira app proxy, which maps `showroom` → inbox and
    // emails the lead via Mailgun. Only the showroom KEY is sent (never an
    // address) so the recipient is resolved server-side.
    const requestBody = {
      showroom,
      company: honeypot,
      first_name: first,
      last_name: last,
      email,
      phone: phoneValue,
      country: phoneCountry || 'za',
      message,
      summary,
      product: product
        ? {
            title: product.title,
            handle: product.handle,
            url: productUrl,
            variant_title: product.variant_title,
            price: quotedValue ?? product.price,
            currency: product.currency,
          }
        : null,
      image_url: details.imageUrl,
      material_name: details.materialName,
      material_swatch_url: details.materialSwatchUrl,
      dimensions: details.dimensions,
      quantity: details.quantity,
      price_display: details.priceDisplay,
      page_url: window.location.href,
      event_id: eventId,
      attribution: collectAttribution(),
    };

    const endpoint = this.dataset.leadEndpoint || '/apps/mira/lead/request-quote';

    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      form.querySelector('.order-by-phone-form__submit')
    );
    const originalLabel = submitBtn?.textContent || '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.dataset.loading = 'true';
      submitBtn.textContent = 'Sending…';
    }
    this.#clearStatus();

    this.#pushDataLayer(payload, 'submit');

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(requestBody),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        /* non-JSON response */
      }
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }

      this.#renderSuccess();
      this.#pushDataLayer(payload, 'success_render');
    } catch (err) {
      console.warn('[obp] lead submit failed', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.dataset.loading = '';
        submitBtn.textContent = originalLabel;
      }
      this.#showError(
        'Sorry — something went wrong sending your request. Please try again, or contact us directly.'
      );
    }
  };

  #renderSuccess() {
    const form = this.#dialog?.querySelector('form');
    if (!form) return;
    const message = this.dataset.successMessage || 'Thanks — your request has been sent.';
    const success = document.createElement('div');
    success.className = 'order-by-phone-form__success';
    success.tabIndex = -1;
    success.textContent = message;
    form.replaceChildren(success);
    requestAnimationFrame(() => success.focus());
  }

  #showError(message) {
    const status = this.#refIn('formStatus');
    if (!status) return;
    status.className = 'order-by-phone-form__status order-by-phone-form__error';
    status.textContent = message;
    status.hidden = false;
  }

  #clearStatus() {
    const status = this.#refIn('formStatus');
    if (!status) return;
    status.className = 'order-by-phone-form__status';
    status.textContent = '';
    status.hidden = true;
  }

  #replayPendingLead() {
    const store = safeStorage();
    if (!store) return;
    let raw = null;
    try {
      raw = store.getItem(PENDING_LEAD_KEY);
    } catch {
      return;
    }
    if (!raw) return;

    let payload = null;
    try {
      payload = JSON.parse(raw);
    } catch {
      store.removeItem(PENDING_LEAD_KEY);
      return;
    }

    this.#pushDataLayer(payload, 'success_render');

    try {
      store.removeItem(PENDING_LEAD_KEY);
    } catch {
      /* ignore */
    }
  }

  #pushDataLayer(payload, phase) {
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];

    const product = payload.product || {};
    const value = parseMoney(payload.price_display) ?? (Number(product.price) || 0);
    const currency = product.currency || 'ZAR';
    const variantId = product.variant_id ? String(product.variant_id) : '';

    window.dataLayer.push({ ecommerce: null });

    window.dataLayer.push({
      event: DATALAYER_EVENT,
      event_id: payload.event_id,
      event_time: payload.event_time,
      event_phase: phase,
      lead_source: payload.lead_source,
      form_id: payload.form_id,

      content_name: product.title || '',
      content_ids: variantId ? [variantId] : [],
      content_type: 'product',
      content_category: product.product_type || '',
      currency,
      value,
      num_items: 1,

      user_data: {
        email_address: payload.user?.email || '',
        phone_number: payload.user?.phone || '',
        address: {
          first_name: payload.user?.first_name || '',
          last_name: payload.user?.last_name || '',
          country: payload.user?.country || '',
        },
      },

      fbp: payload.meta?.fbp || undefined,
      fbc: payload.meta?.fbc || undefined,

      product: {
        id: product.id,
        handle: product.handle,
        title: product.title,
        vendor: product.vendor,
        product_type: product.product_type,
        url: product.url,
        variant_id: product.variant_id,
        variant_title: product.variant_title,
        variant_sku: product.variant_sku,
        price: value,
        compare_at_price: product.compare_at_price || null,
        currency,
        variant_options: payload.variant_options || {},
        collection_handles: typeof product.collection_handles === 'string'
          ? product.collection_handles.split(',').filter(Boolean)
          : [],
      },

      ecommerce: {
        currency,
        value,
        items: [
          {
            item_id: variantId || String(product.id || ''),
            item_name: product.title || '',
            item_variant: product.variant_title || '',
            item_brand: product.vendor || '',
            item_category: product.product_type || '',
            price: value,
            quantity: 1,
          },
        ],
      },

      page: payload.page || {},
    });
  }

  #buildProductSummary() {
    const titleEl = this.#dialog?.querySelector('.order-by-phone-dialog__product-title');
    const title = titleEl?.textContent?.trim() || '';
    const qty = this.#refIn('quantityDisplay')?.textContent?.trim() || '1';
    const price = this.#refIn('priceDisplay')?.textContent?.trim() || '';

    if (!title) return '';

    const lines = [`Product: ${title}`];
    const optionLines = Object.entries(this.#variantSnapshot)
      .filter(([, value]) => value)
      .map(([name, value]) => `  ${name}: ${value}`);
    if (optionLines.length) {
      lines.push('Options:');
      lines.push(...optionLines);
    }
    lines.push(`Quantity: ${qty}`);
    if (price) lines.push(`Price: ${price}`);
    return lines.join('\n');
  }

  // Gathers the visual/spec details shown in the dialog + product page so the
  // lead email can render the chosen variant: the rendered hero image, the
  // selected fabric (name + swatch), the dimensions, qty and price. Everything
  // is read live from the DOM at submit time and degrades to '' when absent
  // (e.g. a non-Mira product with no fabric picker).
  #collectSelectionDetails() {
    const imgEl = this.#dialog?.querySelector('.order-by-phone-dialog__product-image img');
    const imageUrl = imgEl?.currentSrc || imgEl?.getAttribute('src') || '';

    // The Mira configurator publishes the full selection on its root — on a
    // multi-choice product (fabric + frame powder coating) that is the only
    // place the second choice is named.
    const materialName =
      document.querySelector('[data-mira-selection-summary]')?.getAttribute('data-mira-selection-summary')?.trim() ||
      document.querySelector('.mira-fabric-hero__name')?.textContent?.trim() ||
      document.querySelector('.mira-fabric-card--selected .mira-fabric-card__name')?.textContent?.trim() ||
      this.#productData?.variant_title ||
      '';

    const swatchEl =
      document.querySelector('.mira-fabric-hero__img') ||
      document.querySelector('.mira-fabric-card--selected .mira-fabric-card__thumb img');
    const materialSwatchUrl = swatchEl?.currentSrc || swatchEl?.getAttribute('src') || '';

    // Dimensions grid (sofaworx-product-dimensions.js): width=overall width,
    // depth=side, height=total. Each span already reads e.g. "215 cm".
    const dimGrid = document.querySelector('[data-sofaworx-dimensions]');
    const dim = (sel) => dimGrid?.querySelector(sel)?.textContent?.trim() || '';
    const w = dim('[data-dim-width-value]');
    const d = dim('[data-dim-depth-value]');
    const h = dim('[data-dim-height-value]');
    const dimParts = [];
    if (w) dimParts.push(`W ${w}`);
    if (d) dimParts.push(`D ${d}`);
    if (h) dimParts.push(`H ${h}`);

    return {
      imageUrl,
      materialName,
      materialSwatchUrl,
      dimensions: dimParts.join('  ·  '),
      quantity: this.#refIn('quantityDisplay')?.textContent?.trim() || '1',
      priceDisplay: this.#refIn('priceDisplay')?.textContent?.trim() || '',
    };
  }
}

if (!customElements.get('order-by-phone-component')) {
  customElements.define('order-by-phone-component', OrderByPhoneComponent);
}
